import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { Mail, Lock, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [{ title: "Acesso ao Dashboard — Cura pela Alimentação" }],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/dashboard" });
    });
  }, [navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      navigate({ to: "/dashboard" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao autenticar.");
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    setError(null);
    setLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/dashboard",
    });
    if (result.error) {
      setError(
        result.error instanceof Error ? result.error.message : "Falha no Google.",
      );
      setLoading(false);
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/dashboard" });
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-leaf/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-gold/10 blur-3xl" />
      </div>

      <article
        className="relative z-10 w-full max-w-[420px] overflow-hidden rounded-3xl bg-card px-7 py-9"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
          Acesso restrito
        </p>
        <h1 className="mt-2 text-center font-display text-3xl font-bold uppercase text-cream">
          Dashboard
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Entre com sua conta de administrador.
        </p>

        <button
          onClick={onGoogle}
          disabled={loading}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-leaf/40 bg-leaf/10 px-4 py-3 text-sm font-semibold text-cream transition hover:bg-leaf/20 disabled:opacity-50"
        >
          Continuar com Google
        </button>

        <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="h-px flex-1 bg-leaf/20" />
          ou e-mail
          <div className="h-px flex-1 bg-leaf/20" />
        </div>

        <form onSubmit={onSubmit} className="space-y-3" noValidate>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-leaf/70" />
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-mail"
              className="h-12 w-full rounded-2xl bg-cream py-3 pl-11 pr-4 text-sm font-medium text-deep placeholder:text-leaf/60 focus:outline-none focus:ring-2 focus:ring-gold"
              style={{ color: "var(--deep)" }}
            />
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-leaf/70" />
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha"
              className="h-12 w-full rounded-2xl bg-cream py-3 pl-11 pr-4 text-sm font-medium text-deep placeholder:text-leaf/60 focus:outline-none focus:ring-2 focus:ring-gold"
              style={{ color: "var(--deep)" }}
            />
          </div>

          {error && (
            <p className="text-center text-xs" style={{ color: "oklch(0.85 0.12 30)" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl px-5 py-4 text-sm font-extrabold uppercase tracking-wider text-deep transition hover:brightness-110 disabled:opacity-60"
            style={{
              background: "var(--gradient-cta)",
              color: "var(--deep)",
              boxShadow: "var(--glow-gold)",
            }}
          >
            {loading ? <Loader2 className="mx-auto size-5 animate-spin" /> : "Entrar"}
          </button>
        </form>
      </article>
    </main>
  );
}
